import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/animeData";
import { allAnime } from "@/utils/anime";

const values = [
  {
    icon: "ri-file-list-3-line",
    title: "只做资料整理",
    desc: "统一整理作品标题、原名、类型、地区、年份、集数与更新状态，把分散的信息变成可检索的结构化档案。",
  },
  {
    icon: "ri-filter-3-line",
    title: "可筛选可排序",
    desc: "按题材、地区、年份与连载状态组合筛选，并支持按更新时间、热度与字母排序，快速缩小浏览范围。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏演示",
    desc: "收藏功能仅写入当前浏览器的本地存储，用于演示交互，不会上传服务器，也不代表真实账号数据。",
  },
];

const principles = [
  { label: "内容定位", value: "成人向动画作品资料索引" },
  { label: "目标用户", value: "18 岁以上动画爱好者" },
  { label: "内容语言", value: "中文资料整理与介绍" },
  { label: "运营方式", value: "编辑整理 · 定期更新" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 色情动漫内容资源聚合站",
    description: "了解色情动漫网站的定位、内容范围与服务宗旨：专注成人向动画作品的资料整理、分类浏览与检索，不提供播放与下载。",
    keywords: "色情动漫,关于我们,成人动漫,动漫资源站,动漫资料,内容声明",
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="relative w-full overflow-hidden border-b border-background-200/70">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20cinematic%20anime%20scene%20of%20a%20quiet%20archive%20library%20at%20night%20with%20tall%20dark%20wooden%20shelves%2C%20floating%20paper%20cards%2C%20warm%20antique%20gold%20lamps%20and%20deep%20crimson%20curtains%2C%20deep%20crimson%20and%20antique%20gold%20palette%2C%20dark%20atmospheric%20background%2C%20painterly%20detailed%20digital%20illustration%2C%20no%20text&width=1600&height=700&seq=anime-about-hero&orientation=landscape"
            alt="色情动漫资料档案室视觉"
            title="色情动漫 资料档案室"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-400">关于我们</span>
            </nav>
            <p className="text-[11px] tracking-[0.3em] text-accent-500 mb-3">ABOUT US</p>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide max-w-2xl leading-snug">
              把成人向动画作品，
              <br className="hidden md:block" />
              整理成一份可以慢慢翻的档案
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              色情动漫是一个以资料整理为核心的内容聚合站。我们不制作、不发行、不提供任何影片文件，
              只把公开的作品信息整理成统一的格式，帮助成年观众更快判断一部作品是否值得加入片单。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm flex items-center transition-colors whitespace-nowrap"
              >
                浏览全部分类
              </Link>
              <a
                href="mailto:contact@sese-anime.example"
                className="h-10 px-5 rounded-md border border-background-400/60 text-foreground-700 hover:text-accent-400 hover:border-accent-500/70 text-sm flex items-center gap-2 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                联系编辑部
              </a>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 md:gap-12">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide flex items-center gap-2">
                <span className="w-1 h-5 bg-accent-500 rounded-full" />
                我们在做什么
              </h2>
              <div className="mt-4 flex flex-col gap-4 text-sm text-foreground-600 leading-7 max-w-2xl">
                <p>
                  成人向动画长期处在信息碎片化的状态：作品资料散落在各个平台，更新状态不统一，
                  同一部作品在不同站点上甚至会有完全不同的标签。我们想做的，是把这些信息收敛到一处，
                  让「找一部符合当下心情的作品」这件事变得简单一点。
                </p>
                <p>
                  站点目前收录 {allAnime.length} 部作品，覆盖 {categories.length} 个题材分类。
                  每一条记录都包含标题、原名、类型、地区、年份、状态、集数、制作信息、更新日期与内容简介，
                  并支持按标签跳转查看相关内容。
                </p>
                <p>
                  我们不会对作品做出价值评判，也不提供评分之外的推荐承诺。所有榜单均为站内演示数据，
                  目的是帮助你在有限的时间里筛掉明显不合适的选项。
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-300/50 bg-background-100/70 p-5 md:p-6 h-fit">
              <h2 className="text-sm text-foreground-900 tracking-wide mb-4">站点信息</h2>
              <dl className="flex flex-col">
                {principles.map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-4 py-3 border-b border-background-200/60 last:border-b-0">
                    <dt className="text-xs text-foreground-500 shrink-0">{row.label}</dt>
                    <dd className="text-xs text-foreground-800 text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-4 border-t border-background-200/60">
                <p className="text-[11px] text-foreground-400 leading-relaxed">
                  本站为内容展示演示项目，页面中的数据、榜单与热度值均为模拟数据。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 border-y border-background-200/60 bg-background-100/40">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-2">服务宗旨</h2>
          <p className="text-sm text-foreground-500 mb-8 max-w-2xl leading-relaxed">
            用清晰的结构、克制的表达与稳定的更新，帮成年观众省下筛选的时间。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-300/50 bg-background-100/70 p-5 md:p-6">
                <span className="w-10 h-10 rounded-md bg-secondary-900/70 border border-background-300/50 flex items-center justify-center text-accent-400">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <h3 className="mt-4 text-sm text-foreground-900 tracking-wide">{item.title}</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">内容范围</h2>
              <p className="text-sm text-foreground-500 leading-relaxed mb-5">
                目前覆盖以下七个题材方向，每个分类下都有独立的作品集合。点击可进入对应分类页查看筛选结果。
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {categories.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category?category=${encodeURIComponent(item.slug)}`}
                    className="rounded-md border border-background-300/50 bg-background-100/60 px-3 py-2.5 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center gap-2"
                  >
                    <i className={`${item.icon} text-accent-600`} />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 tracking-wide mb-4">联系我们</h2>
              <div className="rounded-lg border border-background-300/50 bg-background-100/60 p-5">
                <a
                  href="mailto:contact@sese-anime.example"
                  className="flex items-center gap-3 group"
                >
                  <span className="w-10 h-10 rounded-md bg-primary-700/60 flex items-center justify-center text-accent-400">
                    <i className="ri-mail-send-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">内容合作与纠错</span>
                    <span className="block text-sm text-foreground-900 group-hover:text-accent-400 transition-colors">
                      contact@sese-anime.example
                    </span>
                  </span>
                </a>

                <div className="mt-5 pt-5 border-t border-background-200/60">
                  <p className="text-xs text-foreground-500 mb-3">社交媒体</p>
                  <div className="flex flex-wrap gap-2.5">
                    <a
                      href="https://0557315.cn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center gap-2"
                    >
                      <i className="ri-twitter-x-line" />
                      X / Twitter
                    </a>
                    <a
                      href="https://t.me"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center gap-2"
                    >
                      <i className="ri-telegram-line" />
                      Telegram
                    </a>
                    <a
                      href="https://discord.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-600 hover:text-accent-400 hover:border-accent-600/60 transition-colors flex items-center gap-2"
                    >
                      <i className="ri-discord-line" />
                      Discord
                    </a>
                  </div>
                  <p className="mt-3 text-[11px] text-foreground-400">点击邮箱将打开你的邮件客户端。</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 pb-6">
          <div className="rounded-lg border border-accent-700/40 bg-accent-500/5 p-5 md:p-7">
            <h2 className="text-sm text-accent-400 tracking-wide flex items-center gap-2 mb-3">
              <i className="ri-shield-check-line" />
              免责声明与版权说明
            </h2>
            <div className="flex flex-col gap-3 text-xs text-foreground-500 leading-relaxed">
              <p>
                本站仅提供作品信息介绍与索引服务，不存储、不制作、不传播任何影片文件，亦不提供播放、下载、付费或充值入口。
                所有作品名称、封面与相关资料的版权归原权利人所有。
              </p>
              <p>
                本站内容仅面向 18 岁以上的成年用户。请确认你所在地区的法律法规允许浏览此类内容，并请勿向未成年人传播本站链接。
                如权利人认为本站信息存在侵权，可通过上方邮箱联系我们，我们会在核实后及时处理。
              </p>
              <p>
                收藏功能与榜单数据均为前端演示，不会上传服务器，也不构成任何形式的会员服务承诺。
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}